import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Mail, Calendar, TrendingUp, TrendingDown, Award,
  BookOpen, Target, Clock, AlertCircle, Bell, BellOff
} from 'lucide-react';
import { client } from '../api/client';
import { useAuthStore } from '../store/authStore';

interface WeeklyReport {
  period: {
    start: string;
    end: string;
  };
  stats: {
    totalPractice: number;
    correctCount: number;
    accuracy: number;
    totalTimeMinutes: number;
  };
  comparison: {
    practiceTrend: number;
    accuracyTrend: number;
    lastWeekTotal: number;
    lastWeekAccuracy: number;
  };
  newBadges: Array<{
    name: string;
    nameCn: string;
    icon: string;
  }>;
}

export function WeeklyReportPage() {
  const { user } = useAuthStore();
  const [report, setReport] = useState<WeeklyReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscribed, setSubscribed] = useState(false);

  useEffect(() => {
    if (user) {
      loadReport();
    }
  }, [user]);

  async function loadReport() {
    setLoading(true);
    try {
      const res = await client.api.fetch('/api/report/weekly');
      const data = await res.json();
      setReport(data.data);
    } catch (error) {
      console.error('Failed to load report:', error);
    } finally {
      setLoading(false);
    }
  }

  async function toggleSubscription() {
    const newState = !subscribed;
    try {
      await client.api.fetch('/api/report/subscribe', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subscribed: newState }),
      });
      setSubscribed(newState);
    } catch (error) {
      console.error('Failed to update subscription:', error);
    }
  }

  if (!user) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-700 mb-2">请先登录</h2>
          <p className="text-slate-500 mb-4">登录后查看学习周报</p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            去登录
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
        >
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">学习周报</h1>
            {report && (
              <p className="text-slate-600 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {report.period.start} - {report.period.end}
              </p>
            )}
          </div>
          <button
            onClick={toggleSubscription}
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${
              subscribed
                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                : 'bg-white text-slate-700 border border-slate-200 hover:border-slate-300'
            }`}
          >
            {subscribed ? (
              <>
                <Bell className="w-4 h-4" />
                已订阅邮件
              </>
            ) : (
              <>
                <BellOff className="w-4 h-4" />
                订阅周报邮件
              </>
            )}
          </button>
        </motion.div>

        {report && (
          <>
            {/* Main Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl p-6 text-white mb-6"
            >
              <h2 className="text-lg font-medium text-white/80 mb-6">本周学习概览</h2>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <BookOpen className="w-4 h-4 text-white/70" />
                    <span className="text-sm text-white/70">练习题数</span>
                  </div>
                  <div className="text-3xl font-bold">{report.stats.totalPractice}</div>
                  <TrendIndicator value={report.comparison.practiceTrend} />
                </div>
                
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Target className="w-4 h-4 text-white/70" />
                    <span className="text-sm text-white/70">正确率</span>
                  </div>
                  <div className="text-3xl font-bold">{report.stats.accuracy}%</div>
                  <TrendIndicator value={report.comparison.accuracyTrend} suffix="%" />
                </div>
                
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="w-4 h-4 text-white/70" />
                    <span className="text-sm text-white/70">学习时长</span>
                  </div>
                  <div className="text-3xl font-bold">{report.stats.totalTimeMinutes}</div>
                  <span className="text-sm text-white/70">分钟</span>
                </div>
                
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Award className="w-4 h-4 text-white/70" />
                    <span className="text-sm text-white/70">正确题数</span>
                  </div>
                  <div className="text-3xl font-bold">{report.stats.correctCount}</div>
                </div>
              </div>
            </motion.div>

            {/* Comparison with Last Week */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl p-6 border border-slate-100 mb-6"
            >
              <h2 className="text-lg font-semibold text-slate-900 mb-4">与上周对比</h2>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-slate-600">练习题数</span>
                    <div className="flex items-center gap-2">
                      {report.comparison.practiceTrend > 0 ? (
                        <TrendingUp className="w-4 h-4 text-emerald-500" />
                      ) : report.comparison.practiceTrend < 0 ? (
                        <TrendingDown className="w-4 h-4 text-red-500" />
                      ) : null}
                      <span className={`font-medium ${
                        report.comparison.practiceTrend > 0 ? 'text-emerald-600' :
                        report.comparison.practiceTrend < 0 ? 'text-red-600' :
                        'text-slate-600'
                      }`}>
                        {report.comparison.practiceTrend > 0 ? '+' : ''}{report.comparison.practiceTrend}%
                      </span>
                    </div>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full"
                      style={{ 
                        width: `${Math.min(100, (report.stats.totalPractice / Math.max(report.comparison.lastWeekTotal, report.stats.totalPractice, 1)) * 100)}%` 
                      }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-slate-500 mt-1">
                    <span>本周: {report.stats.totalPractice}题</span>
                    <span>上周: {report.comparison.lastWeekTotal}题</span>
                  </div>
                </div>
                
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-slate-600">正确率</span>
                    <div className="flex items-center gap-2">
                      {report.comparison.accuracyTrend > 0 ? (
                        <TrendingUp className="w-4 h-4 text-emerald-500" />
                      ) : report.comparison.accuracyTrend < 0 ? (
                        <TrendingDown className="w-4 h-4 text-red-500" />
                      ) : null}
                      <span className={`font-medium ${
                        report.comparison.accuracyTrend > 0 ? 'text-emerald-600' :
                        report.comparison.accuracyTrend < 0 ? 'text-red-600' :
                        'text-slate-600'
                      }`}>
                        {report.comparison.accuracyTrend > 0 ? '+' : ''}{report.comparison.accuracyTrend}%
                      </span>
                    </div>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full"
                      style={{ width: `${report.stats.accuracy}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-slate-500 mt-1">
                    <span>本周: {report.stats.accuracy}%</span>
                    <span>上周: {report.comparison.lastWeekAccuracy}%</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* New Badges */}
            {report.newBadges.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-6 border border-amber-100"
              >
                <h2 className="text-lg font-semibold text-amber-800 mb-4 flex items-center gap-2">
                  <Award className="w-5 h-5" />
                  本周获得徽章
                </h2>
                <div className="flex flex-wrap gap-3">
                  {report.newBadges.map((badge, index) => (
                    <div
                      key={index}
                      className="bg-white rounded-xl px-4 py-2 flex items-center gap-2 shadow-sm"
                    >
                      <Award className="w-5 h-5 text-amber-500" />
                      <span className="font-medium text-amber-700">{badge.nameCn}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Email Subscription Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mt-6 bg-slate-100 rounded-xl p-4 text-center text-sm text-slate-600"
            >
              <Mail className="w-5 h-5 mx-auto mb-2 text-slate-400" />
              {subscribed ? (
                <p>每周一早上8点将向您发送学习周报邮件</p>
              ) : (
                <p>订阅后，每周一早上8点将收到学习周报邮件</p>
              )}
            </motion.div>
          </>
        )}

        {/* Empty State */}
        {!report && (
          <div className="bg-white rounded-2xl p-12 text-center border border-slate-100">
            <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">暂无周报数据</h3>
            <p className="text-slate-500">开始练习后，下周一即可查看学习周报</p>
          </div>
        )}
      </div>
    </div>
  );
}

function TrendIndicator({ value, suffix = '' }: { value: number; suffix?: string }) {
  if (value === 0) return null;
  
  return (
    <span className={`text-sm flex items-center gap-1 ${
      value > 0 ? 'text-emerald-300' : 'text-red-300'
    }`}>
      {value > 0 ? (
        <TrendingUp className="w-3 h-3" />
      ) : (
        <TrendingDown className="w-3 h-3" />
      )}
      {value > 0 ? '+' : ''}{value}{suffix} 较上周
    </span>
  );
}
